import { useMemo } from 'react';
import { API_BASE, authFetch } from '../../lib/rpc';
import { CrmDashboardData } from './crmUtils';

interface UseCrmChatActionsOptions {
  slug?: string;
  authInput?: string;
  data: CrmDashboardData | null;
  setData: React.Dispatch<React.SetStateAction<CrmDashboardData | null>>;
}

export function useCrmChatActions({ slug, authInput, data, setData }: UseCrmChatActionsOptions) {
  const chatDids = useMemo(() => {
    if (!data?.crm) return [];
    const whitelistStr = data.crm.chatDids || '[]';
    let parsed: string[] = [];
    try { parsed = JSON.parse(whitelistStr); } catch {}

    return parsed.map(d => {
      let formatted = d.replace(/\D/g, '');
      if (formatted.length === 11 && formatted.startsWith('1')) formatted = formatted.slice(1);
      if (formatted.length === 10) formatted = `(${formatted.slice(0, 3)}) ${formatted.slice(3, 6)}-${formatted.slice(6)}`;
      else formatted = d;

      return { number: d, didSummary: formatted };
    });
  }, [data?.crm?.chatDids]);

  const defaultChatDid = useMemo(() => {
    if (!data?.crm) return '';
    let parsed: string[] = [];
    try { parsed = JSON.parse(data.crm.chatDids || '[]'); } catch {}
    return data.crm.defaultChatDid || (parsed.length > 0 ? parsed[0] : '');
  }, [data?.crm?.defaultChatDid, data?.crm?.chatDids]);

  const chatFolders = useMemo(() => {
    if (!data?.crm?.chatFolders) return [];
    try { return JSON.parse(data.crm.chatFolders) as string[]; } catch { return []; }
  }, [data?.crm?.chatFolders]);

  const fetchConversations = useMemo(() => async (offset: number = 0, limit: number = 20, did?: string, folder?: string) => {
    if (!slug) return [];
    const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
    let query = `offset=${offset}&limit=${limit}`;
    if (did) query += `&did=${encodeURIComponent(did)}`;
    if (folder) query += `&folder=${encodeURIComponent(folder)}`;
    const res = await authFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/chat/conversations?${query}`, {
      headers: { 'x-crm-password': pw },
      cache: 'no-store'
    });
    return res.json();
  }, [slug]);

  const moveConversations = useMemo(() => async (did: string, contacts: string[], folder: string, rightsPassword?: string) => {
    if (!slug) return;
    const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
    const res = await authFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/chat/folder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-crm-password': pw },
      body: JSON.stringify({ did, contacts, folder, rightsPassword })
    });
    if (!res.ok) throw new Error((await res.json())?.error || 'Failed to move conversations');
    return res.json();
  }, [slug]);

  const manageCustomFolder = useMemo(() => async (action: 'add' | 'remove', folder: string) => {
    if (!slug) return;
    const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
    const res = await authFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/chat/folders/custom`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-crm-password': pw },
      body: JSON.stringify({ action, folder })
    });
    if (!res.ok) throw new Error((await res.json())?.error || 'Failed to manage custom folder');
    const resData = await res.json();
    setData((prev: any) => {
      if (!prev || !prev.crm) return prev;
      return {
        ...prev,
        crm: {
          ...prev.crm,
          chatFolders: JSON.stringify(resData.folders || [])
        }
      };
    });
    return resData;
  }, [slug, setData]);

  const fetchUnreadCounts = useMemo(() => async () => {
    if (!slug) return {};
    const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
    const res = await authFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/chat/unread-counts`, {
      headers: { 'x-crm-password': pw },
      cache: 'no-store'
    });
    return res.json();
  }, [slug]);

  const fetchThreadMessages = useMemo(() => async (contact: string, offset: number = 0, limit: number = 50, did?: string) => {
    if (!contact || !slug) return [];
    const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
    const res = await authFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/chat/messages/${encodeURIComponent(contact)}?offset=${offset}&limit=${limit}${did ? `&did=${encodeURIComponent(did)}` : ''}`, {
      headers: { 'x-crm-password': pw },
      cache: 'no-store'
    });
    return res.json();
  }, [slug]);

  const fetchSearchResults = useMemo(() => async (query: string, limit: number = 20) => {
    if (!query || !slug) return [];
    const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
    const res = await authFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/chat/search?q=${encodeURIComponent(query)}&limit=${limit}`, {
      headers: { 'x-crm-password': pw },
      cache: 'no-store'
    });
    return res.json();
  }, [slug]);

  const fetchExportMessages = useMemo(() => async (contact: string, since: number = 0, did?: string) => {
    if (!contact || !slug) return [];
    const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
    const res = await authFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/chat/export/${encodeURIComponent(contact)}?since=${since}${did ? `&did=${encodeURIComponent(did)}` : ''}`, {
      headers: { 'x-crm-password': pw },
      cache: 'no-store'
    });
    return res.json();
  }, [slug]);

  const resolveCnamMap = useMemo(() => async (numbers: string[]) => {
    try {
      if (!slug) return {};
      const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || authInput || '';
      const payload: any = { numbers, crmSlug: slug, crmPassword: pw || undefined };
      if (data?.crm?.freepbxServerId) payload.serverId = data.crm.freepbxServerId;
      const res = await fetch(`${API_BASE.replace(/\/$/, '')}/api/v1/cnam/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(pw ? { 'x-crm-password': pw } : {}),
          'x-crm-slug': slug || ''
        },
        body: JSON.stringify(payload)
      });
      const dataJson = await res.json();
      const remapped: Record<string, string> = {};
      numbers.forEach(num => {
        const tenDigit = num.replace(/\D/g, '').slice(-10);
        if (dataJson[tenDigit]) {
          remapped[num] = dataJson[tenDigit];
          remapped[tenDigit] = dataJson[tenDigit];
        }
      });
      return remapped;
    } catch (e) {
      console.error("Failed to fetch CNAM bulk", e);
      return {};
    }
  }, [data?.crm?.freepbxServerId, slug, authInput]);

  const deleteConversations = useMemo(() => async (did: string, contacts: string[], rightsPassword?: string) => {
    if (!slug) return;
    const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
    const headers: any = { 'Content-Type': 'application/json', 'x-crm-password': pw };
    if (rightsPassword) headers['x-crm-rights-password'] = rightsPassword;

    const res = await authFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/chat/conversations`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ did, contacts })
    });
    if (!res.ok) {
      const resData = await res.json().catch(() => ({}));
      throw new Error(resData.error || 'Failed to delete conversations');
    }
    return res.json();
  }, [slug]);

  const sendChatMessage = useMemo(() => async (payload: any) => {
    if (!slug) return;
    const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
    const res = await authFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/chat/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-crm-password': pw },
      body: JSON.stringify(payload)
    });
    return res.json();
  }, [slug]);

  const updateCrmSettings = useMemo(() => async (payload: any, adminPassword?: string) => {
    if (!slug) return;
    const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
    const headers: any = { 'Content-Type': 'application/json', 'x-crm-password': pw };
    if (adminPassword) headers['x-crm-admin-password'] = adminPassword;

    const res = await authFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/settings`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      let errorText = 'Failed to update settings';
      try {
        const clonedRes = res.clone();
        const resData = await clonedRes.json();
        errorText = resData.error || errorText;
      } catch {
        errorText = await res.text();
      }
      throw new Error(errorText);
    }

    setData((prev: any) => {
      if (!prev || !prev.crm) return prev;
      return {
        ...prev,
        crm: {
          ...prev.crm,
          ...payload
        }
      };
    });
  }, [slug, setData]);

  const sendChatEvent = useMemo(() => async (payload: { did: string; contact: string; type: 'FLAG' | 'PIN' | 'PRINT'; value: string; agentName: string }, rightsPassword?: string) => {
    if (!slug) return;
    const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
    const headers: any = { 'Content-Type': 'application/json', 'x-crm-password': pw };
    if (rightsPassword) headers['x-crm-rights-password'] = rightsPassword;

    const res = await authFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/chat/event`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const resData = await res.json().catch(() => ({}));
      throw new Error(resData.error || 'Failed to send event');
    }
    return res.json();
  }, [slug]);

  const uploadChatMedia = useMemo(() => async (formData: FormData) => {
    if (!slug) return [];
    const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
    const res = await authFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/chat/upload`, {
      method: 'POST',
      headers: { 'x-crm-password': pw },
      body: formData
    });
    const resData = await res.json();
    if (resData.key) return [resData.key];
    throw new Error(resData.error || "Failed to upload");
  }, [slug]);

  const markMessagesAsRead = useMemo(() => async (messageIds: string[]) => {
    if (!slug) return;
    const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
    const agentName = localStorage.getItem(`crm-agent-${slug}`) || 'Unknown';
    await authFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/chat/read`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-crm-password': pw },
      body: JSON.stringify({ messageIds, readBy: agentName })
    });
  }, [slug]);

  const markMessagesAsUnread = useMemo(() => async (messageIds: string[]) => {
    if (!slug) return;
    const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
    await authFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/chat/unread`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-crm-password': pw },
      body: JSON.stringify({ messageIds })
    });
  }, [slug]);

  return {
    chatDids,
    defaultChatDid,
    chatFolders,
    fetchConversations,
    moveConversations,
    manageCustomFolder,
    fetchUnreadCounts,
    fetchThreadMessages,
    fetchSearchResults,
    fetchExportMessages,
    resolveCnamMap,
    deleteConversations,
    sendChatMessage,
    updateCrmSettings,
    sendChatEvent,
    uploadChatMedia,
    markMessagesAsRead,
    markMessagesAsUnread
  };
}
